import { useState, useCallback, useMemo, useEffect, useRef, useReducer } from "react";
import "@/App.css";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Star, ExternalLink, CheckCircle2, User, Users, Clock, ShieldCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_REVIEW_URL = "https://search.google.com/local/writereview?placeid=ChIJ9SJtOfrDhkcRC5S0JiH96kc";

const QUESTIONS = [
  { id: "q1", category: "Attesa", label: "Come valuti i tempi di attesa?" },
  { id: "q2", category: "Qualit\u00e0", label: "Come valuti la qualit\u00e0 della prestazione?" },
  { id: "q3", category: "Igiene", label: "Come valuti l'igiene dello studio?" },
  { id: "q4", category: "Accoglienza", label: "Come ti sei sentito accolto dal nostro staff?" },
  { id: "q5", category: "Valutazione generale", label: "Nel complesso, che voto dai alla tua esperienza?" },
];

const RATING_LABELS = ["Pessimo", "Mediocre", "Sufficiente", "Buono", "Ottimo"];

const PHONE_PREFIXES = [
  { code: "+39", country: "IT" }, { code: "+41", country: "CH" }, { code: "+43", country: "AT" },
  { code: "+33", country: "FR" }, { code: "+49", country: "DE" }, { code: "+34", country: "ES" },
  { code: "+44", country: "GB" }, { code: "+1", country: "US" }, { code: "+351", country: "PT" },
  { code: "+31", country: "NL" }, { code: "+32", country: "BE" }, { code: "+46", country: "SE" },
  { code: "+47", country: "NO" }, { code: "+48", country: "PL" }, { code: "+40", country: "RO" },
  { code: "+30", country: "GR" }, { code: "+385", country: "HR" }, { code: "+386", country: "SI" },
  { code: "+355", country: "AL" }, { code: "+90", country: "TR" },
];

const INITIAL_RATINGS = { q1: 0, q2: 0, q3: 0, q4: 0, q5: 0 };

// --- Helpers ---

function getDotClassName(isFilled) {
  if (isFilled) return "active bg-[#0f4c81] text-white border-[#0f4c81]";
  return "bg-[#f1f5f9] text-[#64748b] border-[#e2e8f0] hover:bg-[#e2e8f0] hover:border-[#cbd5e1]";
}

function getEdgeLabelClass(isFilled) {
  return isFilled ? "text-[#3b82f6] font-semibold" : "text-[#94a3b8]";
}

function toPatientType(feedbackPer) {
  if (feedbackPer === "me") return "principale";
  if (feedbackPer === "familiare") return "secondario";
  return "";
}

function getIdentityHint(feedbackPer) {
  if (feedbackPer === "me") return "Inserisci i tuoi dati";
  if (feedbackPer === "familiare") return "Inserisci i dati del familiare";
  return "Inserisci i dati";
}

function computeMedia(ratings) {
  const values = Object.values(ratings);
  const filled = values.filter((v) => v > 0);
  if (filled.length === 0) return 0;
  return filled.reduce((a, b) => a + b, 0) / filled.length;
}

// --- Rating dot sublabel ---

function DotLabel({ num, isFilled, isSelected }) {
  if (num === 1) return <span className={`text-[10px] mt-1.5 ${getEdgeLabelClass(isFilled)}`}>Pessimo</span>;
  if (num === 5) return <span className={`text-[10px] mt-1.5 ${getEdgeLabelClass(isFilled)}`}>Ottimo</span>;
  if (isSelected) return <span className="text-[10px] text-[#3b82f6] font-semibold mt-1.5">{RATING_LABELS[num - 1]}</span>;
  return <span className="text-[10px] text-transparent mt-1.5 select-none">&nbsp;</span>;
}

// --- Small components ---

function RatingDots({ value, onChange, questionId }) {
  return (
    <div className="mt-3">
      <div className="flex gap-2.5 sm:gap-3 items-center">
        {[1, 2, 3, 4, 5].map((num) => (
          <div key={num} className="flex flex-col items-center">
            <button type="button" data-testid={`rating-dot-${questionId}-${num}`} onClick={() => onChange(num)}
              className={`rating-dot w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-sm font-semibold border-2 ${getDotClassName(num <= value)}`}>
              {num}
            </button>
            <DotLabel num={num} isFilled={num <= value} isSelected={num === value} />
          </div>
        ))}
      </div>
    </div>
  );
}

function Header() {
  return (
    <header className="fixed top-0 left-0 right-0 h-16 sm:h-20 bg-white/80 backdrop-blur-md z-50 border-b border-[#e2e8f0] flex items-center" data-testid="header">
      <div className="max-w-2xl mx-auto w-full px-4 sm:px-6">
        <img src="https://customer-assets.emergentagent.com/job_smile-rating/artifacts/2msfw7y5_BDD-logo.svg" alt="BDD Studio Dentistico" className="h-8 sm:h-10 w-auto object-contain" data-testid="logo" />
      </div>
    </header>
  );
}

function HeroBanner() {
  return (
    <div className="text-left mb-8 sm:mb-10 step-enter">
      <div className="bg-[#0f4c81] rounded-2xl px-6 py-8 sm:px-8 sm:py-10">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-3 font-['Outfit']" data-testid="main-title">Per noi sei importante!</h1>
        <p className="text-base sm:text-lg text-white/80 max-w-xl leading-relaxed">
          Ti chiediamo 1 minuto del tuo tempo per raccontarci la tua esperienza presso{" "}<strong className="text-white">BDD Studio Dentistico</strong>
        </p>
      </div>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="bg-white p-8 rounded-2xl shadow-sm border border-[#e2e8f0] text-center" data-testid="loading-screen">
      <div className="w-8 h-8 mx-auto border-2 border-[#3b82f6] border-t-transparent rounded-full animate-spin mb-4" />
      <p className="text-[#475569]">Verifica del link in corso...</p>
    </div>
  );
}

function ExpiredScreen({ message }) {
  return (
    <div className="bg-white p-6 sm:p-10 rounded-2xl shadow-sm border border-[#e2e8f0] text-center step-enter" data-testid="expired-screen">
      <div className="mb-6"><div className="w-20 h-20 mx-auto rounded-full bg-amber-50 flex items-center justify-center"><Clock className="w-10 h-10 text-amber-500" /></div></div>
      <h2 className="text-2xl sm:text-3xl font-bold text-[#0f172a] font-['Outfit'] mb-3">Link non valido</h2>
      <p className="text-[#475569] text-base sm:text-lg max-w-md mx-auto leading-relaxed">{message}</p>
    </div>
  );
}

function UsedScreen() {
  return (
    <div className="bg-white p-6 sm:p-10 rounded-2xl shadow-sm border border-[#e2e8f0] text-center step-enter" data-testid="used-screen">
      <div className="mb-6"><div className="w-20 h-20 mx-auto rounded-full bg-blue-50 flex items-center justify-center"><ShieldCheck className="w-10 h-10 text-[#3b82f6]" /></div></div>
      <h2 className="text-2xl sm:text-3xl font-bold text-[#0f172a] font-['Outfit'] mb-3">Feedback gi&agrave; inviato</h2>
      <p className="text-[#475569] text-base max-w-md mx-auto leading-relaxed">Hai gi&agrave; inviato il tuo feedback tramite questo link. Grazie!</p>
    </div>
  );
}

function ThankYouPositive() {
  return (
    <div className="text-center step-enter py-8" data-testid="thank-you-positive">
      <div className="thank-you-icon mb-6"><div className="w-20 h-20 mx-auto rounded-full bg-green-50 flex items-center justify-center"><CheckCircle2 className="w-10 h-10 text-green-500" /></div></div>
      <h2 className="text-2xl sm:text-3xl font-bold text-[#0f172a] font-['Outfit'] mb-3">Grazie!</h2>
      <p className="text-[#475569] text-base sm:text-lg mb-8 max-w-md mx-auto leading-relaxed">Siamo felici che la tua esperienza sia stata positiva. Ti invitiamo a condividere la tua opinione anche su Google!</p>
      <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" data-testid="google-review-btn"
        className="google-btn inline-flex items-center justify-center gap-3 w-full max-w-sm rounded-xl bg-[#0f4c81] px-6 py-4 text-base sm:text-lg font-semibold text-white transition-all hover:bg-[#0a3458] hover:shadow-lg active:scale-[0.98]">
        <Star className="w-5 h-5" />Lascia una recensione su Google<ExternalLink className="w-4 h-4" />
      </a>
    </div>
  );
}

function ThankYouGeneric() {
  return (
    <div className="text-center step-enter py-8" data-testid="thank-you-generic">
      <div className="thank-you-icon mb-6"><div className="w-20 h-20 mx-auto rounded-full bg-blue-50 flex items-center justify-center"><CheckCircle2 className="w-10 h-10 text-[#3b82f6]" /></div></div>
      <h2 className="text-2xl sm:text-3xl font-bold text-[#0f172a] font-['Outfit'] mb-3">Feedback ricevuto, grazie!</h2>
      <p className="text-[#475569] text-base max-w-md mx-auto leading-relaxed">La tua opinione ci aiuter&agrave; a migliorare il nostro servizio.</p>
    </div>
  );
}

// --- Form sub-sections ---

function FeedbackTypeSelector({ feedbackPer, setFeedbackPer }) {
  return (
    <>
      <p className="text-sm font-semibold text-[#0f172a] mb-4">Il feedback &egrave; per: *</p>
      <div className="grid grid-cols-2 gap-3 mb-5">
        <button type="button" data-testid="feedback-per-me" onClick={() => setFeedbackPer("me")}
          className={`flex items-center justify-center gap-2 rounded-xl border-2 px-4 py-3 text-sm font-semibold transition-all active:scale-[0.98] ${
            feedbackPer === "me" ? "border-[#3b82f6] bg-[#eff6ff] text-[#3b82f6]" : "border-[#e2e8f0] bg-white text-[#475569] hover:border-[#cbd5e1]"}`}>
          <User className="w-4 h-4" />Me stesso
        </button>
        <button type="button" data-testid="feedback-per-familiare" onClick={() => setFeedbackPer("familiare")}
          className={`flex items-center justify-center gap-2 rounded-xl border-2 px-4 py-3 text-sm font-semibold transition-all active:scale-[0.98] ${
            feedbackPer === "familiare" ? "border-[#3b82f6] bg-[#eff6ff] text-[#3b82f6]" : "border-[#e2e8f0] bg-white text-[#475569] hover:border-[#cbd5e1]"}`}>
          <Users className="w-4 h-4" />Un familiare
        </button>
      </div>
    </>
  );
}

function PhoneField({ phonePrefix, setPhonePrefix, telefono, setTelefono }) {
  return (
    <div>
      <Label htmlFor="telefono" className="block text-sm font-medium text-[#475569] mb-1.5">Telefono *</Label>
      <div className="flex gap-2">
        <select data-testid="phone-prefix-select" value={phonePrefix} onChange={(e) => setPhonePrefix(e.target.value)}
          className="rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-3 h-12 text-base text-[#0f172a] focus:border-[#3b82f6] focus:ring-2 focus:ring-[#3b82f6]/20 focus:outline-none appearance-none cursor-pointer min-w-[100px]">
          {PHONE_PREFIXES.map((p) => <option key={p.code} value={p.code}>{p.code} {p.country}</option>)}
        </select>
        <Input id="telefono" data-testid="input-telefono" type="tel" placeholder="123 456 7890" value={telefono} onChange={(e) => setTelefono(e.target.value)}
          className="flex-1 rounded-lg border-[#e2e8f0] bg-[#f8fafc] px-4 py-3 h-12 text-base focus:border-[#3b82f6] focus:ring-2 focus:ring-[#3b82f6]/20" />
      </div>
    </div>
  );
}

function IdentityCard({ feedbackPer, setFeedbackPer, nome, setNome, cognome, setCognome, telefono, setTelefono, phonePrefix, setPhonePrefix, email, setEmail }) {
  return (
    <div className="bg-white p-5 sm:p-8 rounded-2xl shadow-sm border border-[#e2e8f0] feedback-card step-enter mb-6" data-testid="identity-card">
      <FeedbackTypeSelector feedbackPer={feedbackPer} setFeedbackPer={setFeedbackPer} />
      <p className="text-sm text-[#94a3b8] mb-3">{getIdentityHint(feedbackPer)}</p>
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="nome" className="block text-sm font-medium text-[#475569] mb-1.5">Nome *</Label>
            <Input id="nome" data-testid="input-nome" placeholder="Nome" value={nome} onChange={(e) => setNome(e.target.value)}
              className="rounded-lg border-[#e2e8f0] bg-[#f8fafc] px-4 py-3 h-12 text-base focus:border-[#3b82f6] focus:ring-2 focus:ring-[#3b82f6]/20" />
          </div>
          <div>
            <Label htmlFor="cognome" className="block text-sm font-medium text-[#475569] mb-1.5">Cognome *</Label>
            <Input id="cognome" data-testid="input-cognome" placeholder="Cognome" value={cognome} onChange={(e) => setCognome(e.target.value)}
              className="rounded-lg border-[#e2e8f0] bg-[#f8fafc] px-4 py-3 h-12 text-base focus:border-[#3b82f6] focus:ring-2 focus:ring-[#3b82f6]/20" />
          </div>
        </div>
        <PhoneField phonePrefix={phonePrefix} setPhonePrefix={setPhonePrefix} telefono={telefono} setTelefono={setTelefono} />
        <div>
          <Label htmlFor="email" className="block text-sm font-medium text-[#475569] mb-1.5">Email <span className="text-[#94a3b8] font-normal">(opzionale)</span></Label>
          <Input id="email" data-testid="input-email" type="email" placeholder="email@esempio.com" value={email} onChange={(e) => setEmail(e.target.value)}
            className="rounded-lg border-[#e2e8f0] bg-[#f8fafc] px-4 py-3 h-12 text-base focus:border-[#3b82f6] focus:ring-2 focus:ring-[#3b82f6]/20" />
        </div>
      </div>
    </div>
  );
}

function RatingsCard({ ratings, onRatingChange }) {
  return (
    <div className="bg-white p-5 sm:p-8 rounded-2xl shadow-sm border border-[#e2e8f0] feedback-card step-enter mb-6" data-testid="ratings-card">
      <div className="space-y-7">
        {QUESTIONS.map((q, idx) => (
          <div key={q.id} className={idx < QUESTIONS.length - 1 ? "pb-6 border-b border-[#f1f5f9]" : ""}>
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#eff6ff] text-[#3b82f6] text-xs font-bold">{idx + 1}</span>
              <span className="text-xs font-semibold text-[#3b82f6] uppercase tracking-wider">{q.category}</span>
            </div>
            <h2 className="text-base sm:text-lg font-semibold text-[#0f172a] font-['Outfit']" data-testid={`question-label-${q.id}`}>{q.label}</h2>
            <RatingDots value={ratings[q.id]} onChange={(val) => onRatingChange(q.id, val)} questionId={q.id} />
          </div>
        ))}
      </div>
    </div>
  );
}

function CommentCard({ comment, setComment }) {
  return (
    <div className="bg-white p-5 sm:p-8 rounded-2xl shadow-sm border border-[#e2e8f0] feedback-card step-enter mb-6" data-testid="comment-card">
      <Label htmlFor="comment" className="block text-sm font-medium text-[#475569] mb-1.5">Vuoi aggiungere un commento?</Label>
      <p className="text-xs text-[#94a3b8] mb-3">Raccontaci cosa potremmo migliorare</p>
      <Textarea id="comment" data-testid="input-comment" placeholder="La tua opinione ci aiuta a migliorare..." value={comment} onChange={(e) => setComment(e.target.value)}
        className="min-h-[100px] rounded-lg border-[#e2e8f0] bg-[#f8fafc] px-4 py-3 text-base focus:border-[#3b82f6] focus:ring-2 focus:ring-[#3b82f6]/20 resize-none" rows={4} />
    </div>
  );
}

function GdprSubmitCard({ gdprPrivacy, setGdprPrivacy, canSubmit, isSubmitting, submitError, onSubmit }) {
  return (
    <div className="bg-white p-5 sm:p-8 rounded-2xl shadow-sm border border-[#e2e8f0] feedback-card step-enter mb-6" data-testid="gdpr-card">
      <div className="space-y-4 p-4 rounded-xl bg-[#f8fafc] border border-[#e2e8f0]">
        <p className="text-sm font-semibold text-[#0f172a]">Privacy e consensi</p>
        <div className="flex items-start gap-3 gdpr-checkbox">
          <Checkbox id="privacy" data-testid="checkbox-privacy" checked={gdprPrivacy} onCheckedChange={(checked) => setGdprPrivacy(!!checked)} className="mt-0.5" />
          <Label htmlFor="privacy" className="text-sm text-[#475569] leading-relaxed cursor-pointer">
            Acconsento al trattamento dei miei dati personali ai sensi del Regolamento UE 2016/679 (GDPR) e del D.Lgs. 196/2003 (Codice Privacy) per le finalit&agrave; connesse alla raccolta del presente feedback. I dati forniti saranno trattati esclusivamente per migliorare la qualit&agrave; del servizio e non saranno comunicati a terzi senza consenso. *
          </Label>
        </div>
      </div>
      {submitError && <p className="mt-4 text-sm text-red-500 text-center" data-testid="submit-error">{submitError}</p>}
      <button data-testid="submit-btn" onClick={onSubmit} disabled={!canSubmit || isSubmitting}
        className="w-full mt-6 rounded-xl bg-[#0f4c81] px-6 py-4 text-base sm:text-lg font-semibold text-white transition-all hover:bg-[#0a3458] hover:shadow-lg active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed">
        {isSubmitting ? "Invio in corso..." : "Invia il feedback"}
      </button>
    </div>
  );
}

// --- Token validation hook ---

function validateTokenResponse(data) {
  if (data.valid) return "form";
  if (data.used) return "used";
  return "expired";
}

function useTokenValidation() {
  const [phase, setPhase] = useState("loading");
  const [token, setToken] = useState(null);
  const [expiredMessage, setExpiredMessage] = useState("");
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const t = new URLSearchParams(window.location.search).get("t");

    if (!t) {
      setExpiredMessage("Per accedere al questionario \u00e8 necessario un link valido. Contatta lo studio per riceverne uno.");
      setPhase("no-token");
      return;
    }

    setToken(t);
    axios.get(`${API}/validate-token`, { params: { token: t } })
      .then((res) => {
        const newPhase = validateTokenResponse(res.data);
        if (newPhase === "expired") setExpiredMessage(res.data.message);
        setPhase(newPhase);
      })
      .catch(() => {
        setExpiredMessage("Errore nella verifica del link. Riprova.");
        setPhase("expired");
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- runs once on mount

  return { phase, setPhase, token, expiredMessage };
}

// --- Form state reducer ---

const FORM_ACTIONS = { SET_FIELD: "SET_FIELD", SET_RATING: "SET_RATING", SWITCH_TYPE: "SWITCH_TYPE" };

function formReducer(state, action) {
  if (action.type === FORM_ACTIONS.SET_RATING) {
    return { ...state, ratings: { ...state.ratings, [action.id]: action.value } };
  }
  if (action.type === FORM_ACTIONS.SET_FIELD) {
    return { ...state, [action.field]: action.value };
  }
  if (action.type === FORM_ACTIONS.SWITCH_TYPE) {
    // Reset personal fields when switching between "me" and "familiare"
    return {
      ...state,
      feedbackPer: action.value,
      nome: "", cognome: "", telefono: "", phonePrefix: "+39", email: "",
    };
  }
  return state;
}

const INITIAL_FORM = {
  ratings: { ...INITIAL_RATINGS },
  feedbackPer: "", nome: "", cognome: "",
  telefono: "", phonePrefix: "+39", email: "",
  comment: "", gdprPrivacy: false,
  isSubmitting: false, submitError: "",
};

function useFeedbackForm(token) {
  const [state, dispatch] = useReducer(formReducer, INITIAL_FORM);
  const { ratings, feedbackPer, nome, cognome, telefono, phonePrefix, email, comment, gdprPrivacy } = state;

  // dispatch is stable (useReducer guarantee), FORM_ACTIONS is module constant
  const set = useCallback((field, value) => dispatch({ type: FORM_ACTIONS.SET_FIELD, field, value }), [dispatch]);
  const setRating = useCallback((id, value) => dispatch({ type: FORM_ACTIONS.SET_RATING, id, value }), [dispatch]);
  const switchType = useCallback((value) => dispatch({ type: FORM_ACTIONS.SWITCH_TYPE, value }), [dispatch]);

  // Derived values from current state
  const allRatingsComplete = Object.values(ratings).every((r) => r > 0);
  const media = useMemo(() => computeMedia(ratings), [ratings]); // computeMedia is a stable module-level function
  const patientType = toPatientType(feedbackPer);
  const fullPhone = telefono.trim() ? `${phonePrefix}${telefono.trim()}` : "";
  const showComment = allRatingsComplete;
  const canSubmit = allRatingsComplete && gdprPrivacy && nome.trim() !== "" && cognome.trim() !== "" && telefono.trim() !== "" && patientType !== "";

  // Use ref for payload to avoid excessive useCallback dependencies
  const stateRef = useRef(state);
  stateRef.current = state;

  const buildPayload = useCallback(() => {
    const s = stateRef.current;
    const phone = s.telefono.trim() ? `${s.phonePrefix}${s.telefono.trim()}` : "";
    return {
      nome: s.nome, cognome: s.cognome, telefono: phone, email: s.email,
      feedback_per: s.feedbackPer,
      answers: Object.entries(s.ratings).map(([id, rating]) => ({ question_id: id, rating })),
      comment: s.comment,
      consenso_privacy: s.gdprPrivacy,
      token: token || null,
    };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps -- stateRef avoids 10+ deps

  return {
    ...state, set, setRating, switchType, allRatingsComplete, media,
    patientType, showComment, canSubmit, buildPayload,
  };
}

// --- Form content ---

function FeedbackForm({ form }) {
  return (
    <>
      <IdentityCard
        feedbackPer={form.feedbackPer} setFeedbackPer={form.switchType}
        nome={form.nome} setNome={(v) => form.set("nome", v)}
        cognome={form.cognome} setCognome={(v) => form.set("cognome", v)}
        telefono={form.telefono} setTelefono={(v) => form.set("telefono", v)}
        phonePrefix={form.phonePrefix} setPhonePrefix={(v) => form.set("phonePrefix", v)}
        email={form.email} setEmail={(v) => form.set("email", v)} />
      <RatingsCard ratings={form.ratings} onRatingChange={form.setRating} />
      {form.showComment && <CommentCard comment={form.comment} setComment={(v) => form.set("comment", v)} />}
    </>
  );
}

// --- Result screen ---

function ResultScreen({ resultType }) {
  if (resultType === "positive") return <ThankYouPositive />;
  return <ThankYouGeneric />;
}

// --- Phase renderer (reduces App cyclomatic complexity) ---

function PhaseRenderer({ phase, expiredMessage, form, handleSubmit, resultType }) {
  if (phase === "loading") return <LoadingScreen />;
  if (phase === "expired" || phase === "no-token") return <ExpiredScreen message={expiredMessage} />;
  if (phase === "used") return <UsedScreen />;

  if (phase === "result") {
    return (
      <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-sm border border-[#e2e8f0] step-enter" data-testid="result-card">
        <ResultScreen resultType={resultType} />
      </div>
    );
  }

  // phase === "form"
  return (
    <>
      <HeroBanner />
      <FeedbackForm form={form} />
      <GdprSubmitCard gdprPrivacy={form.gdprPrivacy} setGdprPrivacy={(v) => form.set("gdprPrivacy", v)}
        canSubmit={form.canSubmit} isSubmitting={form.isSubmitting} submitError={form.submitError} onSubmit={handleSubmit} />
    </>
  );
}

// --- Main App ---

function App() {
  const { phase, setPhase, token, expiredMessage } = useTokenValidation();
  const form = useFeedbackForm(token);
  const [resultType, setResultType] = useState(null);

  // Use ref for form to keep handleSubmit deps minimal
  const formRef = useRef(form);
  formRef.current = form;

  const handleSubmit = useCallback(async () => {
    const f = formRef.current;
    if (!f.canSubmit || f.isSubmitting) return;
    f.set("isSubmitting", true);
    f.set("submitError", "");

    try {
      await axios.post(`${API}/feedback`, f.buildPayload());
      const media = computeMedia(f.ratings);
      setResultType(media >= 4 ? "positive" : "generic");
      setPhase("result");
    } catch (err) {
      const detail = err.response?.data?.detail;
      f.set("submitError", detail || "Si \u00e8 verificato un errore. Riprova tra qualche istante.");
    } finally {
      f.set("isSubmitting", false);
    }
  }, [setPhase]); // eslint-disable-line react-hooks/exhaustive-deps -- formRef avoids stale closure

  return (
    <div className="page-bg">
      <div className="page-content">
        <Header />
        <main className="max-w-2xl mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-12 sm:pb-24">
          <PhaseRenderer phase={phase} expiredMessage={expiredMessage} form={form} handleSubmit={handleSubmit} resultType={resultType} />
          <p className="text-center text-xs text-[#94a3b8] mt-8">&copy; {new Date().getFullYear()} BDD Studio Dentistico. Tutti i diritti riservati.</p>
        </main>
      </div>
    </div>
  );
}

export default App;
